import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Grid, Box, Typography, Divider, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Paper, Chip, CircularProgress, IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function InformeForm() {
    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const detalleId = searchParams.get('detalleId');

    const [detalleInfo, setDetalleInfo] = useState(null);
    const [informe, setInforme] = useState(null);
    const [medicos, setMedicos] = useState([]);
    const [medicoSeleccionado, setMedicoSeleccionado] = useState('');
    const [formData, setFormData] = useState({
        hallazgos: '',
        conclusiones: '',
        comision: 0
    });
    const [loading, setLoading] = useState(false);
    const [medicosLoading, setMedicosLoading] = useState(false);

    // Modal de firma
    const [openFirma, setOpenFirma] = useState(false);
    const [pin, setPin] = useState('');
    const [firmando, setFirmando] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const getInformeId = (data) => data?.id || data?.idInforme;

    const normalizarUsuario20 = (usuario) => {
        const base = (usuario || '').trim();
        if (!base) return '';
        return base.length > 20 ? base.substring(0, 20) : base.padEnd(20, ' ');
    };

    const leerRespuesta = async (response) => {
        const rawText = await response.text();
        if (!rawText) return { rawText: '', data: null };
        try {
            return { rawText, data: JSON.parse(rawText) };
        } catch {
            return { rawText, data: null };
        }
    };

    const fetchDetalleInfo = async () => {
        if (!detalleId || detalleId === 'undefined') return;
        try {
            const response = await fetch(`${server}/MgOrdenesDeTrabajoDetalles/imagenes/detalle-completo/${detalleId}`);
            if (response.ok) {
                const data = await response.json();
                setDetalleInfo(data);
            }
        } catch (error) {
            console.error('Error al cargar detalle:', error);
        }
    };

    const fetchInforme = async () => {
        if (!detalleId || detalleId === 'undefined') return;
        try {
            const response = await fetch(`${server}/ImgInformes/pordetalle/${detalleId}`);
            if (response.ok) {
                const data = await response.json();
                if (data) {
                    setInforme(data);
                    setMedicoSeleccionado(data.idMedico ? String(data.idMedico) : '');
                    setFormData({
                        hallazgos: data.hallazgos || '',
                        conclusiones: data.conclusiones || '',
                        comision: data.comision || 0
                    });
                }
            }
        } catch (error) {
            console.error('Error al cargar informe:', error);
        }
    };

    const fetchMedicos = async () => {
        setMedicosLoading(true);
        try {
            const response = await fetch(`${server}/MgMedicoes`);
            if (!response.ok) throw new Error('No se pudo cargar la lista de médicos');
            const data = await response.json();
            const lista = Array.isArray(data) ? data : (data?.$values || []);
            setMedicos(lista);
        } catch (error) {
            Swal.fire('Error', error.message || 'Error al cargar médicos', 'error');
        } finally {
            setMedicosLoading(false);
        }
    };

    useEffect(() => {
        fetchDetalleInfo();
        fetchInforme();
    }, [detalleId]);

    useEffect(() => {
        fetchMedicos();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!detalleId) {
            Swal.fire('Error', 'Se requiere un ID de detalle', 'error');
            return;
        }
        setLoading(true);
        try {
            const informeId = getInformeId(informe);
            const idMedicoPayload = medicoSeleccionado ? Number(medicoSeleccionado) : (informe?.idMedico || null);
            const usuario20 = normalizarUsuario20(user?.codigoUsuario);
            const payload = {
                ...formData,
                ...(informeId ? { id: informeId } : {}),
                idDetalle: parseInt(detalleId),
                ...(idMedicoPayload ? { idMedico: idMedicoPayload } : {}),
                ...(informe ? { modificadoPor: usuario20 } : { ingresadoPor: usuario20 })
            };
            const method = informe ? 'PUT' : 'POST';
            const url = informe
                ? `${server}/ImgInformes/${informeId}`
                : `${server}/ImgInformes`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const saveBody = await leerRespuesta(response);

            if (response.ok) {
                if (saveBody.data && typeof saveBody.data === 'object') {
                    setInforme(saveBody.data);
                } else if (method === 'PUT') {
                    setInforme(prev => prev ? { ...prev, ...payload } : prev);
                }
                Swal.fire('Éxito', informe ? 'Informe actualizado' : 'Informe creado', 'success');
            } else {
                const detalle =
                    saveBody.data?.message ||
                    saveBody.data?.Message ||
                    saveBody.data?.title ||
                    saveBody.rawText ||
                    'Error al guardar';
                Swal.fire('Error', detalle, 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFirmar = async () => {
        if (!informe) {
            Swal.fire('Validación', 'Primero debe guardar el informe', 'warning');
            return;
        }
        if (!medicoSeleccionado) {
            Swal.fire('Validación', 'Seleccione un médico', 'warning');
            return;
        }
        if (!pin.trim()) {
            Swal.fire('Validación', 'Ingrese su PIN de firma', 'warning');
            return;
        }
        const informeId = getInformeId(informe);
        if (!informeId) {
            Swal.fire('Error', 'No se encontró el ID del informe para firmar', 'error');
            return;
        }
        const pinLimpio = pin.trim();
        if (!/^\d+$/.test(pinLimpio)) {
            Swal.fire('Validación', 'El PIN debe contener solo números', 'warning');
            return;
        }
        setFirmando(true);
        try {
            const usuario20 = normalizarUsuario20(user?.codigoUsuario);
            const payloadUpdate = {
                id: informeId,
                idDetalle: parseInt(detalleId),
                hallazgos: formData.hallazgos,
                conclusiones: formData.conclusiones,
                comision: Number(formData.comision || 0),
                idMedico: Number(medicoSeleccionado),
                modificadoPor: usuario20
            };

            const updateResponse = await fetch(`${server}/ImgInformes/${informeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payloadUpdate)
            });

            const updateBody = await leerRespuesta(updateResponse);

            if (!updateResponse.ok) {
                let detalle = `Error al actualizar médico (HTTP ${updateResponse.status})`;
                detalle =
                    updateBody.data?.message ||
                    updateBody.data?.Message ||
                    updateBody.data?.title ||
                    updateBody.rawText ||
                    detalle;
                Swal.fire('Error', detalle, 'error');
                return;
            }

            if (updateBody.data && typeof updateBody.data === 'object') {
                setInforme(updateBody.data);
            }

            const response = await fetch(`${server}/ImgInformes/${informeId}/verificar-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pin: Number(pinLimpio),
                    realizadoPor: normalizarUsuario20(user?.codigoUsuario),
                    usuarioSistema: normalizarUsuario20(user?.codigoUsuario)
                })
            });

            const verifyBody = await leerRespuesta(response);

            if (response.ok) {
                const verificado = verifyBody.data?.verificado ?? verifyBody.data?.Verificado;
                if (verificado) {
                    // Backend auto-sincroniza: marca informe_imagen_realizado en detalle
                    setOpenFirma(false);
                    setPin('');
                    fetchInforme();
                    Swal.fire('Éxito', 'Informe firmado correctamente', 'success');
                } else {
                    Swal.fire('Error', 'PIN incorrecto', 'error');
                }
            } else {
                let detalle = `Error al verificar PIN (HTTP ${response.status})`;
                detalle =
                    verifyBody.data?.message ||
                    verifyBody.data?.Message ||
                    verifyBody.data?.title ||
                    verifyBody.data?.error ||
                    verifyBody.rawText ||
                    detalle;
                Swal.fire('Error', detalle, 'error');
            }
        } catch (error) {
            const msg = String(error?.message || '');
            const isNetworkOrCors =
                msg.includes('Failed to fetch') ||
                msg.includes('NetworkError') ||
                msg.includes('Load failed') ||
                msg.includes('ERR_FAILED');

            if (isNetworkOrCors) {
                Swal.fire(
                    'Error',
                    'No se pudo completar la verificación por un bloqueo de red/CORS entre localhost y la API. Verifique CORS en backend para POST /ImgInformes/{id}/verificar-pin.',
                    'error'
                );
            } else {
                Swal.fire('Error', msg || 'Error inesperado al firmar', 'error');
            }
        } finally {
            setFirmando(false);
        }
    };

    const handleCerrarPdf = () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
    };

    const handleImprimir = async () => {
        if (!detalleId) return;
        setPdfLoading(true);
        setPdfUrl(null);
        try {
            const response = await fetch(
                `${server}/Whatsapp/ImprimirInformeRayosX/${detalleId}?incluirInforme=${!!informe}`,
                { method: 'POST' }
            );
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                setPdfUrl(url);
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al generar informe', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleEnviarWhatsApp = async () => {
        if (!detalleId) return;
        const { value: formValues } = await Swal.fire({
            title: 'Enviar por WhatsApp',
            html:
                '<input id="swal-input-tel" class="swal2-input" placeholder="Número WhatsApp (ej: 50499887766)" type="tel">' +
                '<label style="display:flex;align-items:center;gap:6px;margin-top:8px;justify-content:center">' +
                `<input id="swal-input-informe" type="checkbox" ${informe ? 'checked' : ''}> Incluir informe radiológico</label>`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Enviar',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const numero = document.getElementById('swal-input-tel').value.trim();
                if (!numero) {
                    Swal.showValidationMessage('Ingrese el número de WhatsApp');
                    return false;
                }
                return {
                    numero,
                    incluirInforme: document.getElementById('swal-input-informe').checked
                };
            }
        });
        if (!formValues) return;
        try {
            Swal.fire({ title: 'Enviando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const params = new URLSearchParams({
                idDetalleOrdenTrabajo: detalleId,
                numeroWhatsapp: formValues.numero,
                incluirInforme: formValues.incluirInforme
            });
            const response = await fetch(
                `${server}/Whatsapp/PostInformeRayosXWhatsapp?${params.toString()}`,
                { method: 'POST' }
            );
            if (response.ok) {
                Swal.fire('Éxito', 'Informe enviado por WhatsApp', 'success');
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al enviar por WhatsApp', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    if (!detalleId) {
        return (
            <div className="right_col" role="main">
            <div className="x_content">
                <div className="x_title"><h2>Informe Radiológico</h2></div>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/ImgWorklist')}
                    sx={{ mb: 2 }}
                >
                    Atrás
                </Button>
                <Typography>Seleccione un detalle de orden para crear/editar el informe.</Typography>
            </div>
            </div>
        );
    }

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <div className="x_title" style={{ marginBottom: 0 }}>
                <h2>Informe Radiológico — Detalle #{detalleId}</h2>
            </div>
            <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/ImgWorklist')}
            >
                Atrás
            </Button>
            </Box>

            {/* Info del detalle */}
            {detalleInfo && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {detalleInfo.paciente && <span><b>Paciente:</b> {typeof detalleInfo.paciente === 'object' ? (detalleInfo.paciente.nombreCompleto || JSON.stringify(detalleInfo.paciente)) : detalleInfo.paciente}</span>}
                    {detalleInfo.producto && <span><b>Examen:</b> {typeof detalleInfo.producto === 'object' ? (detalleInfo.producto.nombre || detalleInfo.producto.descripcion || JSON.stringify(detalleInfo.producto)) : detalleInfo.producto}</span>}
                    {detalleInfo.estadoFlujo && <Chip label={detalleInfo.estadoFlujo} size="small" color={detalleInfo.estadoFlujo === 'informado' ? 'success' : 'info'} />}
                </Box>
            )}

            {informe?.pinVerificado && (
                <Paper sx={{ p: 1, mb: 2, bgcolor: '#e8f5e9' }}>
                    <Typography color="success.main">
                        <LockIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Informe firmado por {informe.idMedico} el {informe.fechaFirma ? new Date(informe.fechaFirma).toLocaleString() : ''}
                    </Typography>
                </Paper>
            )}

            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Hallazgos"
                            name="hallazgos"
                            value={formData.hallazgos}
                            onChange={handleChange}
                            multiline
                            rows={6}
                            disabled={informe?.pinVerificado}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Conclusiones"
                            name="conclusiones"
                            value={formData.conclusiones}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            disabled={informe?.pinVerificado}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Comisión"
                            name="comision"
                            value={formData.comision}
                            onChange={handleChange}
                            inputProps={{ step: 0.01, min: 0 }}
                            disabled={informe?.pinVerificado}
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={
                                    loading ||
                                    informe?.pinVerificado ||
                                    !tienePermiso(informe ? 'Editar Informe Radiologico' : 'Crear Informe Radiologico')
                                }
                            >
                                {loading ? 'Guardando...' : 'Guardar Informe'}
                            </Button>

                            {informe && !informe.pinVerificado && (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<LockIcon />}
                                    onClick={() => setOpenFirma(true)}
                                    disabled={!tienePermiso('Firmar Informe Radiologico')}
                                >
                                    Firmar con PIN
                                </Button>
                            )}

                            {informe && (
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={handleImprimir}
                                    disabled={!tienePermiso('Imprimir Informe Radiologico')}
                                >
                                    Imprimir
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={<WhatsAppIcon />}
                                onClick={handleEnviarWhatsApp}
                                disabled={!tienePermiso('Imprimir Informe Radiologico')}
                            >
                                WhatsApp
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Modal de Firma con PIN */}
            <Dialog open={openFirma} onClose={() => setOpenFirma(false)}>
                <DialogTitle>Firma Digital — Verificar PIN</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Seleccione el médico, ingrese su PIN y confirme la firma del informe radiológico.
                    </Typography>
                    <Autocomplete
                        options={medicos}
                        getOptionLabel={(option) => option?.nombreMedico || ''}
                        isOptionEqualToValue={(option, value) => option?.idMedico === value?.idMedico}
                        value={medicos.find((m) => String(m.idMedico) === String(medicoSeleccionado)) || null}
                        onChange={(_, value) => setMedicoSeleccionado(value ? String(value.idMedico) : '')}
                        loading={medicosLoading}
                        disabled={firmando}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                label="Médico"
                                margin="normal"
                            />
                        )}
                    />
                    <TextField
                        fullWidth
                        type="password"
                        label="PIN"
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleFirmar()}
                        autoFocus
                        inputProps={{ maxLength: 10 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenFirma(false); setPin(''); }}>Cancelar</Button>
                    <Button
                        onClick={handleFirmar}
                        variant="contained"
                        color="warning"
                        disabled={firmando || !pin.trim() || !medicoSeleccionado}
                    >
                        {firmando ? 'Guardando y verificando...' : 'Firmar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal PDF Preview */}
            <Dialog
                open={pdfLoading || !!pdfUrl}
                onClose={handleCerrarPdf}
                fullScreen
                PaperProps={{ style: { display: 'flex', flexDirection: 'column', height: '100vh', margin: 0 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Vista Previa — Informe Rayos X
                    <IconButton onClick={handleCerrarPdf}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent style={{ padding: 0, display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {pdfLoading && (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                            <CircularProgress size={60} />
                            <Typography variant="body1" sx={{ mt: 2 }}>Generando informe...</Typography>
                        </Box>
                    )}
                    {pdfUrl && !pdfLoading && (
                        <iframe
                            src={pdfUrl}
                            style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
                            title="Vista previa del informe radiológico"
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCerrarPdf} variant="outlined" color="secondary">Cerrar</Button>
                </DialogActions>
            </Dialog>
        </div>
        </div>
    );
}

export default InformeForm;
